import { jsx as _jsx } from "react/jsx-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isFinite, startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCollectionQuery } from '@rocketlane/state-db';
import { ProjectAPI } from 'api/project';
import { fetchProjectDetail, getProjectFromCollection, projectCollection, updateProjectRecord, createProjectRecord, deleteProjectRecord, archiveProjectRecord, unarchiveProjectRecord, bulkMergeProjectRecord } from 'collections/project';
import { taskCollection } from 'collections/tasks';
import { toastService } from 'components/molecules/toast';
import { projectCreateStatusQueryKey, useProjectDetail } from 'data/projects';
import { useTranslate } from 'hooks/use-translation';
import { getProjectMembersQueryKey } from 'queries';
import { logger } from 'services';
import { addProjectToTeams } from 'utils/idb-utils';
import { queueMacrotask } from 'utils/macrotask';
import { ProjectId } from '../api/models';
import { ACCOUNT_META_DATA_QUERY_KEY } from './account';
import { getProjectDependencyMapKey, getProjectUsersLiteKey } from './common';
import { tenMinutes, ALL_PROJECTS_QUERY_KEY, ARCHIVED_PROJECTS_QUERY_KEY, PROJECTS_META_QUERY_KEY } from './constants';
import { MEMBERS_V2_QUERY_KEY, MEMBERS_V2_MODIFIED_QUERY_KEY } from './members';
import { useHideNonStrategicPartner } from './partner';
import { PlaceholderResolutionsQueryKey } from './placeholder';
export const getFinancialBudgetsLiteQueryKey = (projectIds)=>[
        'financial-budgets-lite',
        projectIds
    ];
const useProjectDetailQuery = (projectId, options = {})=>{
    var _ref;
    const { isCurrentUserCustomer, isNonStrategicCompany } = useHideNonStrategicPartner();
    const isEnabled = (_ref = options === null || options === void 0 ? void 0 : options.enabled) !== null && _ref !== void 0 ? _ref : Boolean(projectId && projectId[1] !== null && projectId[1] !== -1 && Number.isFinite(projectId[1]));
    // Wave 2 (read-flip): detail is served from projectCollection. The fetcher
    // pushes the full Project; `select` reads it back reactively and applies the
    // per-viewer partner-visibility projection (not stored — a customer must not
    // see non-strategic partners). RQ-only options (retry/refetchOnMount/etc.)
    // no longer apply — the Fetcher handles dedup/staleness/retry.
    return useCollectionQuery({
        collection: projectCollection,
        queryKey: `projects:detail:${projectId === null || projectId === void 0 ? void 0 : projectId[1]}`,
        queryFn: ()=>fetchProjectDetail(projectId[1]),
        select: (c)=>{
            var _project_partners;
            // `select` runs even when the query is disabled / projectId is absent
            // (e.g. the task drawer mounts it with no project). Guard before deref.
            const id = projectId === null || projectId === void 0 ? void 0 : projectId[1];
            if (id == null) {
                return undefined;
            }
            const project = c.get(id);
            if (project == null) {
                return undefined;
            }
            if (!isCurrentUserCustomer()) {
                return project;
            }
            return {
                ...project,
                partners: ((_project_partners = project.partners) !== null && _project_partners !== void 0 ? _project_partners : []).filter((x)=>!isNonStrategicCompany(x))
            };
        },
        enabled: isEnabled,
        staleMs: tenMinutes,
        deps: [
            projectId === null || projectId === void 0 ? void 0 : projectId[1]
        ]
    });
};
const useRefetchProjectDetail = ()=>{
    // Detail is collection-backed (Wave 2). Refresh = refetch+push, which the
    // collection broadcasts to every useProjectDetailQuery consumer reactively.
    return useCallback(async (projectId)=>{
        await fetchProjectDetail(projectId);
    }, []);
};
const useCreateProjectMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation(// createProjectRecord pushes the new Project into projectCollection.
    (payload)=>createProjectRecord(payload), {
        onSuccess: async ()=>{
            // Todo @Muskaan - Remove below 2 queries once pagination goes out
            await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
            await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
            await queryClient.invalidateQueries(MEMBERS_V2_QUERY_KEY);
            await queryClient.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
        },
        ...options
    });
};
const useUpdateProjectMutation = ([, projectId], options = {})=>{
    const queryClient = useQueryClient();
    return useMutation(// updateProjectRecord pushes the server Project into projectCollection, so
    // the detail read updates reactively — the previous setQueryData(detail) is
    // gone (it would be a no-op now). The list invalidations stay (lists are
    // still RQ-backed; cross-pagination).
    (payload)=>updateProjectRecord(projectId, payload), {
        onSuccess: async ()=>{
            // Todo @Muskaan - Remove below 2 queries once pagination goes out
            await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
            await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
        },
        ...options
    });
};
const useExportProjectAuditLogMutation = ()=>{
    return useMutation([
        'export_project_audit_logs'
    ], ({ projectId })=>ProjectAPI.exportProjectAuditLogs(projectId));
};
const useDeleteProjectMutation = (onSuccess)=>{
    const queryClient = useQueryClient();
    return useMutation(// deleteProjectRecord removes the project from projectCollection
    // (externalDelete) after the server confirms.
    ({ projectId })=>deleteProjectRecord(projectId), {
        onSuccess: async ()=>{
            // Todo @Muskaan - Remove below 2 queries once pagination goes out
            await queryClient.refetchQueries(ALL_PROJECTS_QUERY_KEY);
            await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
            if (onSuccess) {
                onSuccess();
            }
        }
    });
};
const useArchiveProjectMutation = (onSuccess)=>{
    const queryClient = useQueryClient();
    return useMutation(// archiveProjectRecord flips the archived flag in projectCollection.
    ({ projectId })=>archiveProjectRecord(projectId), {
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(ARCHIVED_PROJECTS_QUERY_KEY);
            // Todo @Muskaan - Remove below 2 queries once pagination goes out
            await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
            await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
            if (onSuccess) {
                onSuccess();
            }
        }
    });
};
const useUnarchiveProjectMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation(// unarchiveProjectRecord clears the archived flag in projectCollection.
    ({ projectId })=>unarchiveProjectRecord(projectId), {
        onSuccess: ()=>{
            // Queue to next tick so that onSuccess will work fine without causing a re-render
            queueMacrotask(async ()=>{
                await queryClient.invalidateQueries(ARCHIVED_PROJECTS_QUERY_KEY);
                // Todo @Muskaan - Remove below 2 queries once pagination goes out
                await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
                await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
                await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
            });
        }
    });
};
const useProjectBulkMergeMutation = (projectId)=>{
    const queryClient = useQueryClient();
    return useMutation(// bulkMergeProjectRecord pushes the merged Project into the collection.
    (payload)=>bulkMergeProjectRecord(projectId[1], payload), {
        onSuccess: async ()=>{
            await queryClient.refetchQueries(projectCreateStatusQueryKey(projectId[1]));
            // Detail is collection-backed — refetch+push instead of the now-dead
            // invalidateQueries(detail).
            await fetchProjectDetail(projectId[1]);
        // Task-list RQ cache is dead (collection-backed) — the previous
        // invalidateQueries(getProjectTasksQueryKey) was a no-op and is removed.
        }
    });
};
const useProjectBulkMergePreviewMutation = ([, projectId])=>{
    return useMutation((payload)=>ProjectAPI.bulkMergePreview(projectId, payload));
};
const getAllProjectsQueryUpdater = (newProjects)=>{
    const updater = (oldData)=>{
        logger.log('trying to update based on edit success status', newProjects);
        if (!oldData) {
            return oldData;
        }
        return oldData.map((project)=>{
            const predicateByProjectId = ({ projectId })=>project.projectId === projectId;
            const updatedProject = newProjects.find(predicateByProjectId) || project;
            return updatedProject;
        });
    };
    return updater;
};
const useToUpdateProjectsQuery = (queryKey)=>{
    const queryClient = useQueryClient();
    const [projectById, setProjectById] = useState({});
    const updateQueryClient = useRef();
    useEffect(()=>{
        const updateProject = (newProjects)=>{
            queryClient.setQueryData(queryKey, getAllProjectsQueryUpdater(newProjects));
            setProjectById({});
        };
        updateQueryClient.current = updateProject;
    }, [
        queryClient,
        queryKey
    ]);
    const updateProjectToQueryClient = (newProject)=>{
        var _updateQueryClient_current;
        const updatedProjectById = {
            ...projectById,
            [newProject.projectId]: newProject
        };
        setProjectById(updatedProjectById);
        (_updateQueryClient_current = updateQueryClient.current) === null || _updateQueryClient_current === void 0 ? void 0 : _updateQueryClient_current.call(updateQueryClient, Object.values(updatedProjectById));
    };
    return updateProjectToQueryClient;
};
const patchNativeProjectFields = (project, payload)=>{
    const [keyChanged] = Object.keys(payload);
    const [value] = Object.values(payload);
    return {
        ...project,
        [keyChanged]: value
    };
};
const patchProjectByPayload = (project, payload)=>{
    if (!payload) {
        return project;
    }
    if (!payload.fields) {
        return patchNativeProjectFields(project, payload);
    }
    const newProject = JSON.parse(JSON.stringify(project));
    const [payloadField] = payload.fields;
    const predicateByFieldId = (field)=>field.fieldId === payloadField.fieldId;
    const existingProjectField = newProject.fields.find(predicateByFieldId);
    if (!existingProjectField) {
        newProject.fields.push(payloadField);
        return newProject;
    }
    existingProjectField.fieldValue = payloadField.fieldValue;
    return newProject;
};
const useProjectGridToUpdate = ({ onError })=>{
    const queryClient = useQueryClient();
    const updateProjectQuery = useToUpdateProjectsQuery(ALL_PROJECTS_QUERY_KEY);
    const updateProjectMeta = useToUpdateProjectsQuery(PROJECTS_META_QUERY_KEY);
    const translate = useTranslate();
    return useMutation(// updateProjectRecord keeps the write inside the collection layer (pushes the
    // server Project). onSuccess still patches the ALL_PROJECTS/META RQ lists.
    ({ projectId, payload })=>{
        return updateProjectRecord(projectId, payload);
    }, {
        onSuccess: (project)=>{
            updateProjectQuery(project);
            // Detail is collection-backed — push the server Project (replaces the
            // now-dead invalidateQueries(detail)). The ALL_PROJECTS/META RQ lists
            // are still RQ-backed and are patched via updateProjectQuery above.
            projectCollection.push([
                project
            ]);
        },
        onMutate: async ({ projectId, payload })=>{
            var _queryClient_getQueryData, _queryClient_getQueryData1;
            const predicateById = (project)=>project.projectId === projectId;
            const previous = getProjectFromCollection(projectId) || ((_queryClient_getQueryData = queryClient.getQueryData(ALL_PROJECTS_QUERY_KEY)) === null || _queryClient_getQueryData === void 0 ? void 0 : _queryClient_getQueryData.find(predicateById));
            const previousMeta = (_queryClient_getQueryData1 = queryClient.getQueryData(PROJECTS_META_QUERY_KEY)) === null || _queryClient_getQueryData1 === void 0 ? void 0 : _queryClient_getQueryData1.find(predicateById);
            if (previous) {
                const newProject = patchProjectByPayload(previous, payload);
                updateProjectQuery(newProject);
            }
            if (previousMeta && !payload.fields) {
                const newMeta = patchNativeProjectFields(previousMeta, payload);
                updateProjectMeta(newMeta);
            }
            return {
                fallback: {
                    project: previous,
                    meta: previousMeta
                }
            };
        },
        onError: (err, mutationInput, context)=>{
            var _Object_keys, _fallback_project, _err_request;
            const { fallback } = context || {};
            if (fallback === null || fallback === void 0 ? void 0 : fallback.project) {
                updateProjectQuery(fallback.project);
            }
            if (fallback === null || fallback === void 0 ? void 0 : fallback.meta) {
                updateProjectMeta(fallback.meta);
            }
            onError({
                meta: fallback === null || fallback === void 0 ? void 0 : fallback.meta,
                project: fallback === null || fallback === void 0 ? void 0 : fallback.project,
                mutationInput,
                error: err
            });
            const fieldName = startCase((_Object_keys = Object.keys(mutationInput.payload)) === null || _Object_keys === void 0 ? void 0 : _Object_keys[0]);
            const projectName = fallback === null || fallback === void 0 ? void 0 : (_fallback_project = fallback.project) === null || _fallback_project === void 0 ? void 0 : _fallback_project.projectName;
            const errorTitle = translate('project.error.unauth.update.title');
            const errorMsg = translate('project.error.unauth.update.message', {
                fieldName,
                projectName: projectName !== null && projectName !== void 0 ? projectName : '',
                bold: (chunks)=>/*#__PURE__*/ _jsx("strong", {
                        children: chunks
                    })
            });
            // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
            if (err instanceof Error && (err === null || err === void 0 ? void 0 : (_err_request = err.request) === null || _err_request === void 0 ? void 0 : _err_request.status) === 403) {
                toastService({
                    title: errorTitle,
                    message: errorMsg
                }).error();
            }
        },
        retry: false
    });
};
const addTeamsAPI = (projectId, teams)=>ProjectAPI.addTeams(projectId, teams);
const useAddTeamsToProjectMutation = (options = {})=>useMutation(async ({ projectId, teamIds, projectFinancialsBudgetIds })=>{
        const team = await addTeamsAPI(projectId, teamIds.map((teamId)=>({
                teamId,
                projectFinancialsBudgetIds
            })));
        addProjectToTeams({
            projectId,
            teamIds
        }).catch((err)=>{
            logger.error(`Error occured while adding projectId to team. teamIds: ${teamIds}`, err);
        });
        return team;
    }, options);
const removeTeamsAPI = (projectId, teams)=>ProjectAPI.removeTeams(projectId, teams);
const useRemoveTeamsFromProjectMutation = (options = {})=>useMutation(({ projectId, teamIds })=>removeTeamsAPI(projectId, teamIds.map((teamId)=>({
                teamId
            }))), options);
const useProjectUsersLiteQuery = (queryOptions = {})=>useQuery(getProjectUsersLiteKey(), ()=>ProjectAPI.getAllProjectUsersLite().then((data)=>{
            const lookup = {};
            data.forEach(({ projectId, members })=>{
                var _lookup_projectId;
                lookup[projectId] = ((_lookup_projectId = lookup[projectId]) !== null && _lookup_projectId !== void 0 ? _lookup_projectId : []).concat(members);
            });
            return lookup;
        }), {
        cacheTime: 0,
        refetchOnWindowFocus: false,
        ...queryOptions
    });
const useProjectUsersLiteForProjectQuery = (projectId, queryOptions = {})=>useQuery(getProjectUsersLiteKey(projectId), ()=>ProjectAPI.getProjectUsersLiteForProject(projectId), {
        refetchOnWindowFocus: false,
        ...queryOptions
    });
const filterExistingProjectUsers = (projectId, projectUser, oldState = {})=>{
    var _ref;
    var _oldState_projectId;
    const { user: { userId }, role: projectUserRole, referenceTeam: projectUserReferenceTeam } = projectUser;
    return (_ref = (_oldState_projectId = oldState[projectId]) === null || _oldState_projectId === void 0 ? void 0 : _oldState_projectId.filter(({ user, role, referenceTeam })=>!(user.userId === userId && (role === null || role === void 0 ? void 0 : role.roleId) === (projectUserRole === null || projectUserRole === void 0 ? void 0 : projectUserRole.roleId) && (referenceTeam === null || referenceTeam === void 0 ? void 0 : referenceTeam.teamId) === (projectUserReferenceTeam === null || projectUserReferenceTeam === void 0 ? void 0 : projectUserReferenceTeam.teamId)))) !== null && _ref !== void 0 ? _ref : [];
};
const useAddProjectUserLiteToQueryState = ()=>{
    const queryClient = useQueryClient();
    return useCallback((projectId, projectUser)=>{
        queryClient.setQueryData(getProjectUsersLiteKey(), (state = {})=>({
                ...state,
                [projectId]: filterExistingProjectUsers(projectId, projectUser, state).concat(projectUser)
            }));
    }, [
        queryClient
    ]);
};
const useRemoveProjectUserLiteFromQueryState = ()=>{
    const queryClient = useQueryClient();
    return useCallback((projectId, projectUser)=>{
        queryClient.setQueryData(getProjectUsersLiteKey(), (state = {})=>({
                ...state,
                [projectId]: filterExistingProjectUsers(projectId, projectUser, state)
            }));
    }, [
        queryClient
    ]);
};
const useAddProjectUsersLiteMutation = (options = {})=>useMutation(({ projectId, projectUsers, source, assignedToProjectMember })=>ProjectAPI.addProjectUsersLite(projectId, {
            members: projectUsers,
            source,
            assignedToProjectMember
        }), options);
const useRemoveProjectUsersLiteMutation = (options = {})=>useMutation(({ projectId, projectUsers: usersReplacingTeams, source })=>ProjectAPI.removeProjectUsersLite(projectId, {
            members: usersReplacingTeams,
            source
        }), options);
const useAddPartnersToProjectMutation = (options = {})=>{
    const translate = useTranslate();
    return useMutation(({ projectId, partners })=>ProjectAPI.addPartners(projectId, partners), {
        onSuccess: (_, params)=>{
            // Detail is collection-backed — refetch+push refreshes partners.
            fetchProjectDetail(params.projectId);
            toastService({
                title: translate('project.invitation.add_partners.success.title', {
                    count: params.partners.length
                }),
                message: ''
            }).success();
        },
        onError: (_, params)=>{
            toastService({
                title: translate('project.invitation.add_partners.error.title', {
                    count: params.partners.length
                }),
                message: ''
            }).error();
        },
        ...options
    });
};
const useRemovePartnersToProjectMutation = (options = {})=>{
    return useMutation(({ projectId, partners })=>ProjectAPI.removePartners(projectId, partners), {
        onSuccess: (_, params)=>{
            // Detail is collection-backed — refetch+push refreshes partners.
            fetchProjectDetail(params.projectId);
        },
        ...options
    });
};
export const useBulkShiftDatesMutation = (options = {})=>useMutation(({ projectId, payload })=>ProjectAPI.bulkShiftDates(projectId, payload), options);
export const getProjectFinancialsQueryKey = (projectId, budgetId = -1, includeSubscriptionMeta = false)=>[
        'project-financials',
        projectId,
        budgetId,
        includeSubscriptionMeta
    ];
export const useProjectFinancialsQuery = (projectId, includeSubscriptionMeta, subscriptionPeriodId, budgetId, queryParams, queryOptions = {})=>useQuery(getProjectFinancialsQueryKey(projectId, budgetId, includeSubscriptionMeta), ()=>ProjectAPI.getProjectFinancials({
            projectId,
            includeSubscriptionMeta,
            subscriptionPeriodId,
            queryParams,
            budgetId
        }), {
        ...queryOptions,
        cacheTime: 0
    });
export const getProjectFinancialsTrackerQueryKey = (projectId)=>[
        'project-financials-tracker',
        projectId
    ];
export const useProjectFinancialsTrackerQuery = (projectId, queryOptions = {})=>useQuery(getProjectFinancialsTrackerQueryKey(projectId), ()=>// @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        ProjectAPI.getProjectFinancialsData(`/projects/${projectId}/financials/trackers`), {
        ...queryOptions,
        cacheTime: 0
    });
export const getProjectPermissionsQueryKey = (projectId)=>[
        'project-permissions',
        projectId
    ];
export const useProjectPermissionsQuery = (projectId, queryOptions = {})=>useQuery(getProjectPermissionsQueryKey(projectId), ()=>ProjectAPI.getProjectPermissionsForCurrentUser(projectId), {
        ...queryOptions
    });
export const RateCardsProjectSettingsQueryKey = (projectId)=>[
        'rate-cards-project-settings',
        projectId
    ];
export const RateCardsProjectSettingsQueryKeyV2 = (projectId)=>[
        'rate-cards-project-settings-v2',
        projectId
    ];
/**
 * @deprecated use the new V2 query after multi line budgets
 */ export const useProjectsBudgetAndRateSettingsQuery = (projectId, queryOptions = {})=>useQuery(RateCardsProjectSettingsQueryKey(projectId), ()=>ProjectAPI.getProjectBudgetSettings(projectId), {
        ...queryOptions,
        cacheTime: tenMinutes
    });
export const useProjectsBudgetAndRateSettingsV2Query = (projectId, queryOptions = {})=>{
    var _queryOptions_enabled;
    return useQuery(RateCardsProjectSettingsQueryKeyV2(projectId), ()=>ProjectAPI.getProjectBudgetSettingsV2(projectId), {
        ...queryOptions,
        enabled: !!projectId && projectId !== -1 && ((_queryOptions_enabled = queryOptions.enabled) !== null && _queryOptions_enabled !== void 0 ? _queryOptions_enabled : true)
    });
};
export const ProjectUserTeamsQueryKey = (projectId)=>[
        'project-user-teams',
        projectId
    ];
export const useProjectUserTeamsQuery = (projectId, queryOptions = {})=>useQuery(ProjectUserTeamsQueryKey(projectId), ()=>ProjectAPI.getProjectUsersLiteForProject(projectId), {
        ...queryOptions
    });
export const ProjectUserTeamsCostRates = (projectId)=>[
        'project-user-team-cost-rates',
        projectId
    ];
export const useProjectUserTeamsCostRatesQuery = (projectId, queryOptions = {})=>useQuery(ProjectUserTeamsCostRates(projectId), ()=>ProjectAPI.getProjectUserTeamsCostRates(projectId), {
        ...queryOptions,
        cacheTime: 0
    });
/**
 * @deprecated use the new V2 query after multi line budgets
 */ export const useProjectsBudgetAndRateSettingsMutation = (projectId, options = {})=>useMutation((payload)=>ProjectAPI.updateProjectBudgetSettings(projectId, payload), {
        ...options
    });
export const useUpdateProjectGeneralSettingsV2Mutation = (projectId, options = {})=>useMutation((payload)=>ProjectAPI.updateProjectGeneralSettingsV2(projectId, payload), {
        ...options
    });
const useCreateProjectBudgetMutationV2 = (projectId, options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>ProjectAPI.createProjectBudgetSettingsV2(projectId, payload), {
        ...options,
        onSuccess: (...props)=>{
            var _options_onSuccess;
            // Invalidate budget exclusions queries for the project
            queryClient.invalidateQueries([
                'project-members-exclusion',
                ProjectId(projectId)
            ]);
            queryClient.invalidateQueries([
                'budget-member-exclusion'
            ]);
            options === null || options === void 0 ? void 0 : (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, ...props);
        }
    });
};
export const useProjectsBudgetAndRateSettingsMutationV2 = (projectId, options = {})=>{
    const queryClient = useQueryClient();
    return useMutation(({ budgetId, payload, resetRateOverrides = false })=>ProjectAPI.updateProjectBudgetSettingsV2(projectId, budgetId, payload, resetRateOverrides), {
        ...options,
        onSuccess: (...props)=>{
            var _options_onSuccess;
            // const existingPayload = queryClient.getQueryData(
            //   RateCardsProjectSettingsQueryKeyV2(projectId),
            // ) as ProjectFinancialsDetails;
            // const newPayload = {
            //   ...existingPayload,
            //   projectFinancialsBudgets: (
            //     existingPayload.projectFinancialsBudgets ?? []
            //   ).map((d) => {
            //     if (
            //       d.projectFinancialsBudgetId === data.projectFinancialsBudgetId
            //     ) {
            //       return data;
            //     }
            //     return d;
            //   }),
            // } as ProjectFinancialsDetails;
            // queryClient.setQueryData(
            //   RateCardsProjectSettingsQueryKeyV2(projectId),
            //   newPayload,
            // );
            queryClient.invalidateQueries(RateCardsProjectSettingsQueryKeyV2(projectId));
            queryClient.invalidateQueries(getFinancialBudgetsLiteQueryKey([
                projectId
            ]));
            // Invalidate budget exclusions queries for the project
            queryClient.invalidateQueries([
                'project-members-exclusion',
                ProjectId(projectId)
            ]);
            queryClient.invalidateQueries([
                'budget-member-exclusion'
            ]);
            options === null || options === void 0 ? void 0 : (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, ...props);
        }
    });
};
export const useBudgetDeleteMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>ProjectAPI.deleteProjectBudget(payload), {
        ...options,
        onSuccess: (_, payload, ...props)=>{
            var _options_onSuccess;
            // Invalidate budget exclusions queries for the project
            queryClient.invalidateQueries([
                'project-members-exclusion',
                ProjectId(payload.projectId)
            ]);
            queryClient.invalidateQueries([
                'budget-member-exclusion'
            ]);
            options === null || options === void 0 ? void 0 : (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, _, payload, ...props);
        }
    });
};
export const useMarkBudgetAsDefaultMutation = (projectId, options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((budgetId)=>ProjectAPI.markBudgetAsDefault(projectId, budgetId), {
        ...options,
        onSuccess: (...props)=>{
            var _options_onSuccess;
            // Invalidate budget exclusions queries for the project
            queryClient.invalidateQueries([
                'project-members-exclusion',
                ProjectId(projectId)
            ]);
            queryClient.invalidateQueries([
                'budget-member-exclusion'
            ]);
            options === null || options === void 0 ? void 0 : (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, ...props);
        }
    });
};
export const BudgetMemberExclusionQueryKey = (budgetId)=>[
        'budget-member-exclusion',
        budgetId
    ];
export const useGetBudgetMemberExclusions = (projectId, budgetId, queryOptions = {})=>useQuery(BudgetMemberExclusionQueryKey(budgetId), ()=>ProjectAPI.getMemberExclusionsForBudget(projectId, budgetId), {
        ...queryOptions,
        cacheTime: 0
    });
export const ProjectBudgetExclusionsQueryKey = (projectId)=>[
        'project-budget-exclusions',
        projectId
    ];
export const useGetProjectBudgetExclusions = (projectId, queryOptions = {})=>{
    var _queryOptions_enabled;
    return useQuery(ProjectBudgetExclusionsQueryKey(projectId !== null && projectId !== void 0 ? projectId : -1), ()=>{
        return ProjectAPI.getMemberExclusionsForProject(projectId !== null && projectId !== void 0 ? projectId : -1);
    }, {
        ...queryOptions,
        enabled: !!projectId && ((_queryOptions_enabled = queryOptions.enabled) !== null && _queryOptions_enabled !== void 0 ? _queryOptions_enabled : true),
        staleTime: tenMinutes
    });
};
const useUpdateFinancialBudgetMembersMutation = (projectId, options = {})=>{
    const queryClient = useQueryClient();
    return useMutation(({ projectId: projId, budgetId, payload })=>ProjectAPI.updateFinancialBudgetMembers(projId, budgetId, payload), {
        ...options,
        onSuccess: ()=>{
            queryClient.invalidateQueries(getProjectMembersQueryKey(ProjectId(projectId)));
            queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
            queryClient.invalidateQueries(RateCardsProjectSettingsQueryKeyV2(projectId));
            // Invalidate budget exclusions queries for the project
            queryClient.invalidateQueries([
                'project-members-exclusion',
                ProjectId(projectId)
            ]);
            queryClient.invalidateQueries([
                'budget-member-exclusion'
            ]);
            queryClient.invalidateQueries(ProjectBudgetExclusionsQueryKey(projectId));
        }
    });
};
const useAddMembersToBudgetMutation = (projectId, options = {})=>{
    const queryClient = useQueryClient();
    return useMutation(({ budgetIds, users, teams })=>ProjectAPI.addMembersToBudget({
            budgetIds,
            users,
            teams
        }), {
        ...options,
        onSuccess: (data, variables)=>{
            queryClient.invalidateQueries(getProjectMembersQueryKey(ProjectId(projectId)));
            queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
            queryClient.invalidateQueries(RateCardsProjectSettingsQueryKeyV2(projectId));
            // Invalidate budget exclusions queries for the project
            queryClient.invalidateQueries([
                'project-members-exclusion',
                ProjectId(projectId)
            ]);
            queryClient.invalidateQueries([
                'budget-member-exclusion'
            ]);
            // Invalidate budget members cache for all selected budgets
            variables.budgetIds.forEach((budgetId)=>{
                queryClient.invalidateQueries([
                    'budget-members',
                    projectId,
                    budgetId
                ]);
            });
            // Call the original onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables, undefined);
            }
        }
    });
};
const useDeleteBudgetMember = (options = {})=>{
    return useMutation(({ projectId, budgetId, payload })=>ProjectAPI.deleteBudgetMember(projectId, budgetId, payload), {
        ...options,
        onSuccess: (_, variables, ...props)=>{
            var _options_onSuccess;
            options === null || options === void 0 ? void 0 : (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, _, variables, ...props);
        }
    });
};
export const useProjectCostRateSettingsMutation = (projectId, options = {})=>useMutation(({ updatedPayload, backdate, resetRateOverrides = false })=>ProjectAPI.updateProjectCostRates(projectId, backdate, updatedPayload, resetRateOverrides), {
        ...options
    });
const useShiftTasksMutation = (projectId)=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>ProjectAPI.shiftTasks(projectId, payload), {
        onSuccess: async (response)=>{
            if (response) {
                taskCollection.push(response.updatedTasks);
            }
            // Detail is collection-backed — refetch+push (shiftTasks can move phase
            // dates on the project). Replaces the now-dead invalidateQueries(detail).
            await fetchProjectDetail(projectId);
            // Task-list RQ cache is dead (collection-backed) — shifted tasks already
            // land reactively via taskCollection.push above, so the previous
            // invalidateQueries(getProjectTasksQueryKey) is removed.
            await queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));
            return response;
        }
    });
};
const useReorderPhasesMutation = (projectId)=>// Thin: the optimistic reorder, verify/reconcile and rollback live at the
    // call site (RearrangePhasesModal → reorderProjectPhasesOptimistic), mirroring
    // the phase-edit modal's optimistic-close flow. Project detail is
    // collection-backed on state-db, so there is no query key to invalidate.
    useMutation((payload)=>ProjectAPI.reorderPhases(projectId, payload));
const useGetDependenciesMap = (projectId)=>{
    return useQuery(getProjectDependencyMapKey(projectId), ()=>ProjectAPI.getDependenciesMap(projectId), {
        cacheTime: 0,
        enabled: isFinite(Number(projectId))
    });
};
export const useFinancialBudgetsLiteQuery = (projectIds, queryOptions = {})=>useQuery(getFinancialBudgetsLiteQueryKey(projectIds), ()=>ProjectAPI.getFinancialBudgetsLite(projectIds), {
        enabled: projectIds.length > 0,
        ...queryOptions
    });
const useProjectDetailsWithAllBudgetFields = (projectId)=>{
    var _financialsQuery_data;
    const projectQuery = useProjectDetail(ProjectId(projectId));
    const financialsQuery = useProjectsBudgetAndRateSettingsV2Query(projectId);
    // Memoize merged data so same reference when underlying data unchanged.
    // Prevents ProjectPlan memo from failing on parent re-renders (e.g. hash change).
    // Ref cache: when projectId unchanged and we have data, return cached — React Query
    // returns new refs on refetch/focus; hash-only re-renders (task drawer) don't change data.
    // Never return cached when loading (next undefined) — avoids blank page on navigation.
    const mergedDataRef = useRef();
    const mergedData = useMemo(()=>{
        var _projectQuery_data_projectFinancialsDetailModel;
        var _financialsQuery_data;
        const next = projectQuery.data ? {
            ...projectQuery.data,
            projectFinancialsDetailModel: {
                ...(_projectQuery_data_projectFinancialsDetailModel = projectQuery.data.projectFinancialsDetailModel) !== null && _projectQuery_data_projectFinancialsDetailModel !== void 0 ? _projectQuery_data_projectFinancialsDetailModel : {},
                projectFinancialsBudgets: (_financialsQuery_data = financialsQuery.data) === null || _financialsQuery_data === void 0 ? void 0 : _financialsQuery_data.projectFinancialsBudgets
            }
        } : undefined;
        mergedDataRef.current = next;
        return next;
    }, [
        projectQuery.data,
        (_financialsQuery_data = financialsQuery.data) === null || _financialsQuery_data === void 0 ? void 0 : _financialsQuery_data.projectFinancialsBudgets
    ]);
    return {
        ...projectQuery,
        data: mergedData,
        isLoading: projectQuery.isLoading || financialsQuery.isLoading
    };
};
export { useProjectDetailQuery, useRefetchProjectDetail, useCreateProjectMutation, useUpdateProjectMutation, useExportProjectAuditLogMutation, useDeleteProjectMutation, useArchiveProjectMutation, useUnarchiveProjectMutation, useProjectBulkMergeMutation, useProjectBulkMergePreviewMutation, useProjectGridToUpdate, useAddTeamsToProjectMutation, useRemoveTeamsFromProjectMutation, useProjectUsersLiteQuery, useProjectUsersLiteForProjectQuery, useAddProjectUserLiteToQueryState, useRemoveProjectUserLiteFromQueryState, useAddProjectUsersLiteMutation, useRemoveProjectUsersLiteMutation, useAddPartnersToProjectMutation, useRemovePartnersToProjectMutation, useShiftTasksMutation, useReorderPhasesMutation, useGetDependenciesMap, useUpdateFinancialBudgetMembersMutation, useAddMembersToBudgetMutation, useDeleteBudgetMember, useCreateProjectBudgetMutationV2, useProjectDetailsWithAllBudgetFields };
